import { defineStore } from 'pinia'
import { ref } from 'vue'
import { useSuiteStore } from '@/stores/useSuiteStore'

export interface CommercialOption {
  uuid: string
  name: string
  email: string
  bu: string | null
}

export interface SemesterColumn {
  /** Clé technique, ex: `2026-S1`. */
  key: string
  /** Libellé affiché, ex: `S1 2026`. */
  label: string
}

export interface FamilyStatRow {
  code: string
  label: string
  bySemester: Record<string, number>
  total: number
}

export interface TotalRow {
  bySemester: Record<string, number>
  total: number
}

/** Détail d'un commercial : sa marge par famille × semestre + son total. */
export interface CommercialDetail {
  uuid: string
  name: string
  email: string
  familyRows: FamilyStatRow[]
  totalRow: TotalRow
  total: number
}

export interface FamilyStatsResult {
  semesters: SemesterColumn[]
  familyRows: FamilyStatRow[]
  totalRow: TotalRow
  commercialDetails: CommercialDetail[]
  scope: { commercialUuids: string[]; orderCount: number }
  generatedAt: string
}

/**
 * Stats commerciales par famille / semestre (espace manager).
 * Source : boost-core `GET /sales-family-stats/*` (périmètre = managés récursifs
 * du caller, garanti côté serveur).
 */
export const useSalesFamilyStatsStore = defineStore('salesFamilyStats', () => {
  const suiteStore = useSuiteStore()

  const commercials = ref<CommercialOption[]>([])
  const result = ref<FamilyStatsResult | null>(null)
  const isLoading = ref(false)
  const isLoadingCommercials = ref(false)
  const error = ref<string | null>(null)

  async function fetchCommercials(): Promise<void> {
    isLoadingCommercials.value = true
    error.value = null
    try {
      const res = await suiteStore.suiteFetch('sales-family-stats/commercials', {
        method: 'GET',
        credentials: 'include',
      })
      if (res && res.ok) {
        commercials.value = (await res.json()) ?? []
      } else {
        error.value = res ? `Erreur ${res.status}` : 'Erreur réseau'
      }
    } catch (e) {
      error.value = e instanceof Error ? e.message : 'Erreur inconnue'
    } finally {
      isLoadingCommercials.value = false
    }
  }

  async function fetchData(commercialUuids: string[] = []): Promise<void> {
    isLoading.value = true
    error.value = null
    try {
      const q = new URLSearchParams()
      commercialUuids.forEach((u) => q.append('commercialUuids', u))
      const qs = q.toString()
      const res = await suiteStore.suiteFetch(`sales-family-stats/data${qs ? `?${qs}` : ''}`, {
        method: 'GET',
        credentials: 'include',
      })
      if (res && res.ok) {
        result.value = await res.json()
      } else {
        error.value = res ? `Erreur ${res.status}` : 'Erreur réseau'
        result.value = null
      }
    } catch (e) {
      error.value = e instanceof Error ? e.message : 'Erreur inconnue'
      result.value = null
    } finally {
      isLoading.value = false
    }
  }

  return {
    commercials,
    result,
    isLoading,
    isLoadingCommercials,
    error,
    fetchCommercials,
    fetchData,
  }
})
